import { useState, useRef, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { formatDistanceToNow, format, isToday, isYesterday, differenceInHours } from 'date-fns';
import { Pencil, Trash2, MessageSquare, SmilePlus } from 'lucide-react';
import type { ChatMessage } from './useChatMessages';
import MessageAttachments from './MessageAttachments';
import EmojiPicker from './EmojiPicker';
import { Avatar, Button } from '../ui';
import { useCoarsePointer } from './useCoarsePointer';

interface MessageBubbleProps {
  message: ChatMessage;
  isGrouped: boolean; // true if same user as previous message within 10min
  showTimeSeparator: boolean;
  timeSeparatorLabel?: string;
  onEdit?: (messageId: string, newContent: string) => Promise<void>;
  onDelete?: (messageId: string) => Promise<void>;
  onReplyInThread?: (messageId: string) => void;
  onAddReaction?: (messageId: string, emoji: string) => void;
  replyCount?: number;
  lastReplyAt?: string;
}

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  const hoursDiff = differenceInHours(new Date(), date);

  if (hoursDiff < 1) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  if (isToday(date)) {
    return format(date, 'h:mm a');
  }
  if (isYesterday(date)) {
    return 'Yesterday ' + format(date, 'h:mm a');
  }
  return format(date, 'MMM d, h:mm a');
}

function formatSeparatorDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMMM d');
}

function UserAvatar({ name, avatar, size = 'md', isOwlbert = false }: { name: string; avatar: string | null; size?: 'sm' | 'md'; isOwlbert?: boolean }) {
  const dimension = size === 'sm' ? 24 : 32;

  // Owlbert keeps the owl emoji as its avatar (user content)
  if (isOwlbert && !avatar) {
    return (
      <span
        className="rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: dimension,
          height: dimension,
          background: 'var(--status-warn-soft)',
          border: '1px solid var(--border-hairline)',
        }}
      >
        <span style={{ fontSize: size === 'sm' ? '11px' : '14px' }}>🦉</span>
      </span>
    );
  }

  return <Avatar name={name} src={avatar} size={dimension} />;
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkBreaks]}
      components={{
        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="hover-underline"
            style={{ color: 'var(--accent-text)' }}
          >
            {children}
          </a>
        ),
        code: ({ children, className }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code
                className="px-1.5 py-0.5"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-xs)',
                  color: 'var(--text-body)',
                }}
              >
                {children}
              </code>
            );
          }
          return (
            <code
              className="block p-3 my-1.5 overflow-x-auto"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-body)',
              }}
            >
              {children}
            </code>
          );
        },
        strong: ({ children }) => (
          <strong style={{ fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'] }}>{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => (
          <ul className="list-disc list-inside ml-1 mb-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside ml-1 mb-1">{children}</ol>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

const hoverActionStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  border: 'none',
  background: 'transparent',
  borderRadius: 'var(--radius-xs)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};

// Touch: thumb-sized buttons
const touchActionStyle: React.CSSProperties = { ...hoverActionStyle, width: '40px', height: '36px' };

function HoverActions({
  onEdit,
  onDelete,
  onReplyInThread,
  onAddReaction,
  isOwn,
  touch = false,
  touchOpen = false,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  onReplyInThread: () => void;
  onAddReaction?: () => void;
  isOwn: boolean;
  /** Touch device: hover never fires, so the bar shows on tap instead. */
  touch?: boolean;
  touchOpen?: boolean;
}) {
  // On touch the bar sits in the flow under the message (an absolute bar
  // over the line above gets clipped at the top of the scroller), and only
  // exists while the message is tapped open.
  if (touch && !touchOpen) return null;
  const btnStyle = touch ? touchActionStyle : hoverActionStyle;
  return (
    <div
      className={
        touch
          ? 'inline-flex items-center gap-0.5 mt-1.5'
          : 'absolute -top-3 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity'
      }
      style={{
        padding: '2px',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-rest)',
      }}
    >
      {onAddReaction && (
        <button
          onClick={(e) => { e.stopPropagation(); onAddReaction(); }}
          className="hover-wash pressable"
          style={btnStyle}
          title="Add reaction"
          aria-label="Add reaction"
        >
          <SmilePlus size={touch ? 16 : 13} />
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onReplyInThread(); }}
        className="hover-wash pressable"
        style={btnStyle}
        title="Reply in thread"
        aria-label="Reply in thread"
      >
        <MessageSquare size={touch ? 16 : 13} />
      </button>
      {isOwn && onEdit && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="hover-wash pressable"
          style={btnStyle}
          title="Edit message"
          aria-label="Edit message"
        >
          <Pencil size={touch ? 16 : 13} />
        </button>
      )}
      {isOwn && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="hover-wash pressable"
          style={{ ...btnStyle, color: 'var(--danger-400)' }}
          title="Delete message"
          aria-label="Delete message"
        >
          <Trash2 size={touch ? 16 : 13} />
        </button>
      )}
    </div>
  );
}

function DeleteConfirmation({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 mt-1"
      style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}
    >
      <span>Delete this message?</span>
      <Button variant="danger" size="sm" onClick={onConfirm}>
        Yes
      </Button>
      <Button variant="secondary" size="sm" onClick={onCancel}>
        No
      </Button>
    </div>
  );
}

function ThreadIndicator({
  replyCount,
  lastReplyAt,
  onClick,
}: {
  replyCount: number;
  lastReplyAt: string;
  onClick: () => void;
}) {
  return (
    <div className="flex items-baseline gap-2 mt-1">
      <button
        onClick={onClick}
        className="hover-underline pressable"
        style={{
          border: 'none',
          background: 'transparent',
          padding: '2px 0',
          color: 'var(--accent-text)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
          cursor: 'pointer',
        }}
      >
        {replyCount} {replyCount === 1 ? 'reply' : 'replies'} →
      </button>
      <span className="t-num" style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
        last reply {formatDistanceToNow(new Date(lastReplyAt), { addSuffix: true })}
      </span>
    </div>
  );
}

function InlineEditInput({
  initialContent,
  onSave,
  onCancel,
}: {
  initialContent: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialContent);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(value.length, value.length);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && value.trim() !== initialContent) {
        onSave(value.trim());
      } else if (value.trim() === initialContent) {
        onCancel();
      }
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="mt-0.5">
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={Math.min(value.split('\n').length, 6)}
        className="ck-textarea"
        style={{ resize: 'none' }}
      />
      <div className="flex items-center gap-1.5 mt-1">
        <Button
          size="sm"
          icon="check"
          onClick={() => {
            if (value.trim() && value.trim() !== initialContent) {
              onSave(value.trim());
            } else {
              onCancel();
            }
          }}
        >
          Save
        </Button>
        <Button size="sm" variant="secondary" icon="x" onClick={onCancel}>
          Cancel
        </Button>
        <span
          className="t-num ml-1"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}
        >
          Esc to cancel · Enter to save
        </span>
      </div>
    </div>
  );
}

function MessageContent({
  message,
  isOwn,
  isEditing,
  isConfirmingDelete,
  onStartEdit,
  onStartDelete,
  onCancelEdit,
  onSaveEdit,
  onConfirmDelete,
  onCancelDelete,
  onReplyInThread,
  onAddReaction,
  replyCount,
  lastReplyAt,
  touch,
  touchOpen,
}: {
  message: ChatMessage;
  isOwn: boolean;
  isEditing: boolean;
  isConfirmingDelete: boolean;
  onStartEdit?: () => void;
  onStartDelete?: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (content: string) => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onReplyInThread: () => void;
  onAddReaction?: () => void;
  replyCount?: number;
  lastReplyAt?: string;
  touch?: boolean;
  touchOpen?: boolean;
}) {
  const isDeleted = !!message.deleted_at;

  if (isDeleted) {
    return (
      <div className="italic" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
        [This message was deleted]
      </div>
    );
  }

  return (
    <>
      {isEditing ? (
        <InlineEditInput
          initialContent={message.content}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      ) : (
        <>
          {message.content && (
            <div
              className="mt-0.5 chat-markdown"
              style={{
                // long URLs otherwise run off the right edge on a phone
                overflowWrap: 'anywhere',
                fontSize: 'var(--text-base)',
                lineHeight: 'var(--leading-normal)',
                color: 'var(--text-body)',
              }}
            >
              <MarkdownContent content={message.content} />
            </div>
          )}
          {/* Inline attachments from message metadata */}
          {message.metadata?.attachments && (
            <MessageAttachments attachments={message.metadata.attachments} />
          )}
        </>
      )}
      {message.edited_at && !isEditing && (
        <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
          (edited)
        </span>
      )}
      {/* Thread indicator - shown BEFORE reactions would be */}
      {!isEditing && !isConfirmingDelete && !!replyCount && replyCount > 0 && lastReplyAt && (
        <ThreadIndicator
          replyCount={replyCount}
          lastReplyAt={lastReplyAt}
          onClick={onReplyInThread}
        />
      )}
      {isConfirmingDelete && (
        <DeleteConfirmation
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      )}
      {!isDeleted && !isEditing && !isConfirmingDelete && (
        <HoverActions
          onEdit={onStartEdit}
          onDelete={onStartDelete}
          onReplyInThread={onReplyInThread}
          onAddReaction={onAddReaction}
          isOwn={isOwn}
          touch={touch}
          touchOpen={touchOpen}
        />
      )}
    </>
  );
}

export default function MessageBubble({
  message,
  isGrouped,
  showTimeSeparator,
  timeSeparatorLabel,
  onEdit,
  onDelete,
  onReplyInThread,
  onAddReaction,
  replyCount,
  lastReplyAt,
}: MessageBubbleProps) {
  const { user } = useUser();
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Callback-ref state: the anchor div mounts in the same render the picker
  // opens, so a plain ref would still be null when Popover first positions.
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLDivElement | null>(null);

  const touch = useCoarsePointer();
  // Touch: tapping a message opens its action bar; tapping elsewhere closes it
  const [touchOpen, setTouchOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!touchOpen) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setTouchOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    // The bar adds height under the message; on the last message it would
    // open below the fold right above the composer
    const raf = requestAnimationFrame(() =>
      rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    );
    return () => {
      document.removeEventListener('pointerdown', onDown);
      cancelAnimationFrame(raf);
    };
  }, [touchOpen]);
  const handleTap = (e: React.MouseEvent) => {
    if (!touch || isEditing || isConfirmingDelete) return;
    // Links, buttons, attachments and the edit box keep their own taps
    if ((e.target as HTMLElement).closest('a, button, textarea, input, img, video, audio')) return;
    setTouchOpen(o => !o);
  };
  // Any action from the bar closes it
  const closeAfter = (fn?: () => void) =>
    fn ? () => { setTouchOpen(false); fn(); } : undefined;

  const isOwn = message.user_id === user?.id;
  const isOwlbert = message.user.name.toLowerCase().includes('owlbert');

  const handleStartEdit = () => {
    setIsConfirmingDelete(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = async (newContent: string) => {
    if (onEdit) {
      try {
        await onEdit(message.id, newContent);
      } catch {
        // error handled in hook
      }
    }
    setIsEditing(false);
  };

  const handleStartDelete = () => {
    setIsEditing(false);
    setIsConfirmingDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (onDelete) {
      try {
        await onDelete(message.id);
      } catch {
        // error handled in hook
      }
    }
    setIsConfirmingDelete(false);
  };

  const handleCancelDelete = () => {
    setIsConfirmingDelete(false);
  };

  const handleReplyInThread = () => {
    if (onReplyInThread) {
      onReplyInThread(message.id);
    }
  };

  const handleToggleEmojiPicker = onAddReaction ? () => {
    setShowEmojiPicker(prev => !prev);
  } : undefined;

  const handleEmojiSelect = (emoji: string) => {
    if (onAddReaction) {
      onAddReaction(message.id, emoji);
    }
    setShowEmojiPicker(false);
  };

  return (
    <>
      {/* Day separator: hairline — eyebrow date — hairline */}
      {showTimeSeparator && timeSeparatorLabel && (
        <div className="flex items-center gap-3 my-4 px-3.5">
          <span className="flex-1 h-px" style={{ background: 'var(--border-hairline)' }} />
          <span className="t-eyebrow">{timeSeparatorLabel}</span>
          <span className="flex-1 h-px" style={{ background: 'var(--border-hairline)' }} />
        </div>
      )}

      {/* Message */}
      <div
        ref={rootRef}
        className={`group relative hover-wash px-3.5 py-0.5 ${isGrouped ? '' : 'mt-3'}`}
        onMouseEnter={() => setShowTimestamp(true)}
        onMouseLeave={() => { setShowTimestamp(false); }}
        onClick={handleTap}
        style={touchOpen ? { background: 'var(--surface-active)' } : undefined}
      >
        {!isGrouped ? (
          // Full message with avatar and name
          <div className="flex items-start gap-3">
            <UserAvatar name={message.user.name} avatar={message.user.avatar} isOwlbert={isOwlbert} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                    color: isOwlbert ? 'var(--accent-text)' : 'var(--text-body)',
                  }}
                >
                  {message.user.name}
                  {isOwlbert && ' 🦉'}
                </span>
                <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                  {formatTimestamp(message.created_at)}
                </span>
              </div>
              <MessageContent
                message={message}
                isOwn={isOwn}
                isEditing={isEditing}
                isConfirmingDelete={isConfirmingDelete}
                onStartEdit={closeAfter(handleStartEdit)}
                onStartDelete={closeAfter(handleStartDelete)}
                onCancelEdit={handleCancelEdit}
                onSaveEdit={handleSaveEdit}
                onConfirmDelete={handleConfirmDelete}
                onCancelDelete={handleCancelDelete}
                onReplyInThread={closeAfter(handleReplyInThread) ?? handleReplyInThread}
                onAddReaction={closeAfter(handleToggleEmojiPicker)}
                replyCount={replyCount}
                lastReplyAt={lastReplyAt}
                touch={touch}
                touchOpen={touchOpen}
              />
            </div>
          </div>
        ) : (
          // Grouped message (no avatar/name, compact)
          <div className="flex items-start gap-3">
            {/* Spacer for alignment with avatar */}
            <div className="w-8 flex-shrink-0 flex items-center justify-center">
              {(showTimestamp || touchOpen) && (
                <span className="t-num" style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
                  {format(new Date(message.created_at), 'h:mm')}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <MessageContent
                message={message}
                isOwn={isOwn}
                isEditing={isEditing}
                isConfirmingDelete={isConfirmingDelete}
                onStartEdit={closeAfter(handleStartEdit)}
                onStartDelete={closeAfter(handleStartDelete)}
                onCancelEdit={handleCancelEdit}
                onSaveEdit={handleSaveEdit}
                onConfirmDelete={handleConfirmDelete}
                onCancelDelete={handleCancelDelete}
                onReplyInThread={closeAfter(handleReplyInThread) ?? handleReplyInThread}
                onAddReaction={closeAfter(handleToggleEmojiPicker)}
                replyCount={replyCount}
                lastReplyAt={lastReplyAt}
                touch={touch}
                touchOpen={touchOpen}
              />
            </div>
          </div>
        )}

        {/* Emoji picker triggered from hover actions */}
        {showEmojiPicker && (
          <div ref={setEmojiAnchor} className="ml-[44px] mt-1 relative">
            <EmojiPicker
              visible={showEmojiPicker}
              anchorEl={emojiAnchor}
              onSelect={handleEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
            />
          </div>
        )}
      </div>
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { formatSeparatorDate };
